import { getEnvs } from '@/shared/utils';
const { VITE_BUCKET_BASE } = getEnvs();
import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
  plantillaExcelENUM,
} from '@/shared';
import {
  useFetchSectors,
  useUpdateSector,
} from '@/shared/actions/erp/sectors.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
  useUploadFile,
} from '@/shared/hooks';
import { useUiConfirmModalStore } from '@/store/ui';
import { Sector } from '@/shared/interfaces';
import { SAVE_SECTOR_PERMISSIONS } from '@/erp/sytem-sttings/sectors/shared/constants';



export type SectorsPageProps = {};

const SectorsPage: React.FC<SectorsPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_sector);
  const returnUrlSectorsPage =
    ROUTER_PATHS.mantenimientoOperaciones.sectoresNav;
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: sectorsPagingRes,
    isLoading,
    isRefetching,
  } = useFetchSectors({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updSate = useUpdateSector({
    enableRedirect: false,
  });

  ///* handlers
  const onEdit = (origin: Sector) => {
    navigate(`${returnUrlSectorsPage}/editar/${origin.id_sector}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Sector>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 270,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },

      // // fk ---------------------
      {
        accessorKey: 'pais_nombre', // to allow filtering
        header: 'País',
        size: 200,
        Cell: ({ row }) => row.original?.pais_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'provincia_nombre',
        header: 'Provincia',
        size: 200,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'ciudad_nombre',
        header: 'Ciudad',
        size: 200,
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'zona_nombre',
        header: 'Zona',
        size: 200,
        Cell: ({ row }) => row.original?.zona_data?.nombre || 'N/A',
      },

      {
        accessorKey: 'cobertura',
        header: 'Cobertura',
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.cobertura}
              titulo={row.original.cobertura ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_sector)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado Cobertura',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original?.id_sector!,
                      data: { cobertura: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      //
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_sector)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_sector!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  const {
    fileInputRef,
    handleImportarExcelSectorCobertura
  } = useUploadFile();

  return (
    <SingleTableBoxScene
      title="Sectores"
      urlRedirect={`${returnUrlSectorsPage}/crear`}

      enableRedirect={hasPermission(PermissionsEnum.api_add_sector)}

      enableUploadFile={true}
      fileInputRef={fileInputRef}
      handleFileUpload={handleImportarExcelSectorCobertura}
      urlDownloadPlantilla={"/"+VITE_BUCKET_BASE+plantillaExcelENUM.PLANTILLA_SECTOR}
      urlDownloadNameExcel='plantilla_sector'
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<Sector>
        columns={columns}
        data={sectorsPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={sectorsPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          ...SAVE_SECTOR_PERMISSIONS,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          ...SAVE_SECTOR_PERMISSIONS,
        ])}
        onEdit={onEdit}
        canDelete={false}
      // onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default SectorsPage;